<script lang="ts">
import DkOlMap from '@nuxtDatakodeUtils/components/dk-ol-map/dk-ol-map.vue'
import DkOlLayers from '@nuxtDatakodeUtils/components/dk-ol-map/layers/dk-ol-layers.vue'
import DkOlPrint from '@nuxtDatakodeUtils/components/dk-ol-map/controls/dk-ol-print.vue'
import DkOlMeasure from '@nuxtDatakodeUtils/components/dk-ol-map/controls/dk-ol-measure.vue'
import DkOlLegend from '@nuxtDatakodeUtils/components/dk-ol-map/controls/dk-ol-legend.vue'
import DkOlGeosearch from '@nuxtDatakodeUtils/components/dk-ol-map/controls/dk-ol-geosearch.vue'
import i3pSubComponent from '@nuxtDatakodeUtils/components/i3p/I3pSubComponent.vue'
import type { FeatureLike } from 'ol/Feature'
import { useTheme } from 'vuetify'
import MapTooltip from '@nuxtDatakodeUtils/components/i3p/panels/map-tooltip.vue'
import { OpportunityMapService } from '~/services/opportunityMap.service'
import OpportunitiesLayer from '~/components/I3pComponent/opportunities/OpportunitiesLayer.vue'
import IntentionsLayer from '~/components/I3pComponent/intentions/IntentionsLayer.vue'

// noinspection JSUnusedGlobalSymbols
export default {
    name: 'IntentionMapPanel',
    components: {
        MapTooltip,
        DkOlPrint,
        DkOlLegend,
        DkOlMap,
        DkOlLayers,
        // DkOlScale,
        DkOlMeasure,
        DkOlPrint,
        DkOlGeosearch,
        OpportunitiesLayer,
        IntentionsLayer,
    },
    extends: i3pSubComponent,
    data() {
        return {
            mapService: null as OpportunityMapService,
            popupFeature: null,
            popupContent: null as string,
            tooltipFeatures: [] as FeatureLike[],
        }
    },
    computed: {
        applicationService(): ApplicationService {
            return this.$applicationService as ApplicationService
        },
        editMode(): boolean {
            return this.i3pService.editMode
        },
        bbox(): { latitude_min: number, latitude_max: number, longitude_min: number, longitude_max: number } {
            return this.applicationService.getBBox()
        },
        legend(): Legend {
            return this.$refs.legend.legend
        },
        translationName(): string {
            return this.i3pService.currentEntity.translationKey
        },
        currentFeature(): FeatureLike {
            return this.$refs.opportunitiesLayer?.currentFeature
        },
    },
    created() {
        this.theme = useTheme()
    },
    mounted(): void {
        this.mapService = new OpportunityMapService((this.$refs.dkmap as DkOlMap)?.map)
        this.mapService.setLegend(this.legend)
    },
    methods: {
        bboxFromCurrentFeature(): FeatureLike | null {
            if (this.currentFeature) {
                return this.currentFeature.getGeometry().getExtent()
            }
            return null
        },
        hoverFeatures(event: { features: FeatureLike[] }) {
            const features = event.features
            const properties = features[0]?.getProperties()
            if (properties)
                this.tooltipFeatures = features
            else
                this.tooltipFeatures = []
            // this.popupInfo(features[0])
        },
        clickFeatures(event: { features: FeatureLike[] }) {
            const features = event.features
            this.i3pService.showEntity(features[0].get('k'))
        },
        popupInfo(feature: FeatureLike): void {
            const properties = feature?.getProperties()
            if (properties !== undefined) {
                let content = `<h3>${properties?.name}</h3><table>`
                for (const key of feature?.getKeys()) {
                    if (['id', 'name', 'status'].includes(key)) {
                        const title = this.$translate(`${this.translationName}.${key}`)
                        const value = feature?.get(key)
                        const translatedValue = this.$translate(value)
                        const isStatus = key === 'status'
                        const style = 'text-align: left; padding-right: 2px;'
                        content
                            += `<tr>
                                <th style="text-align: left; padding-right: 2px;">${title}</th>
                                <td style="${isStatus ? this.getChipStyle(value) : style}">${translatedValue}</td>
                            </tr>`
                    }
                }
                content += '</table>'

                this.popupFeature = feature
                this.popupContent = content
                this.$refs.popup.show()
            }
            else {
                this.$refs.popup.hide()
            }
        },
        zoomOnCurrentItem(): void {
            this.$refs.opportunitiesLayer?.centerOnCurrentEntity()
        },
        displayAll(): void {
            this.$refs.opportunitiesLayer?.displayAll()
        },
        getChipStyle(value: string): string {
            const hexOrVuetifyColor = this.$getColor(`${this.translationName}.status.${value}`)
            const hexColor = this.resolveColor(hexOrVuetifyColor)
            return [
                'display:inline-block',
                'padding:2px 10px',
                'border-radius:9999px',
                `background:${hexColor}`,
                `color:white`,
                'font-size:12px',
                'line-height:18px',
                'font-weight:600',
                'white-space:nowrap',
            ].join(';')
        },
        resolveColor(colorKeyOrHex: string): string {
            const themeColors = this.theme.current?.value?.colors
            return themeColors?.[colorKeyOrHex] || colorKeyOrHex
        },
    },
}
</script>

<template>
    <div class="dk-ol-map has-hover mb-4 position-relative">
        <DkOlMap v-if="!applicationService.loading" ref="dkmap" :bbox>
            <DkOlLayers :layers="applicationService.getMapLayers()" />
            <OpportunitiesLayer
                ref="opportunitiesLayer"
                :map-service
                :i3p-service
                @hover-features="hoverFeatures($event)"
                @click-features="!editMode && clickFeatures($event)"
            />

            <IntentionsLayer
                ref="intentionsLayer"
                :map-service
                :i3p-service
            />

            <!--            <DkOlPopup ref="popup" :feature="popupFeature" :html="popupContent" /> -->

            <ol-fullscreen-control tip-label="Mettre en plein écran" source="fullscreen" />
            <DkOlLegend ref="legend" />

            <ol-layerswitcher-control />
            <ol-scaleline-control bar />

            <!--            <ol-printdialog-control title="TEST" :pdf="false" lang="fr" /> -->

            <DkOlMeasure />

            <DkOlGeosearch
                :xmin="bbox.longitude_min"
                :ymin="bbox.latitude_min"
                :xmax="bbox.longitude_max"
                :ymax="bbox.latitude_max"
            />

            <DkOlPrint />
        </DkOlMap>
        <MapTooltip
            v-if="!i3pService.editMode && i3pService.currentEntity?.translationKey" ref="mapTooltip"
            :features="tooltipFeatures" :translation-name="i3pService.currentEntity?.translationKey"
        />
    </div>
</template>

<style>
.dk-ol-map {
    &.has-hover {
        cursor: pointer;
    }

    div.ol-legend {
        z-index: 15;
        min-width: 200px;
    }

    div.ol-legend > canvas.ol-legendImg {
        background: #fff;
    }

    .ol-scale-bar {
        margin-left: 40px;
    }

    .ol-layerswitcher {
        z-index: 10;
    }

    .ol-popup-content {
        max-width: 400px;
    }
}
</style>
